// Author: Sri Pooja
// Date: 10/04/2024
import { getHeaders } from './headers';
import { redirectToMainAndRefresh } from '../../Utils/sessionRedirect';
const GET_MESSAGE = 'Data retrieved successfully.';
const POST_MESSAGE = 'Saved Successfully.';
const PUT_MESSAGE = 'Updated successfully.';
const DELETE_MESSAGE = 'Data deleted successfully.';
const ERROR_MESSAGE = 'An error occurred while processing your request.';
// A 401 almost always means the session/token went stale under this tab. Send the browser back
// to the main route and hard-refresh so the app re-initializes from a clean slate, rather than
// hitting the external logout flow for what may just be a transient token issue.
const redirectOnUnauthorized = redirectToMainAndRefresh;
export const get = async (url, addAlerts, startLoading, stopLoading, customMessage) => {
    if (startLoading)
        startLoading();
    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: getHeaders(url),
        });
        if (response.status === 401) {
            redirectOnUnauthorized();
            return null;
        }
        // Parse the body defensively so valid content is returned even when the Content-Type
        // isn't JSON (a 406 body that fails JSON.parse must not fall through to the catch block).
        const raw = await response.text();
        let data = null;
        if (raw) {
            try {
                data = JSON.parse(raw);
            }
            catch {
                data = raw;
            }
        }
        // 200-range and 406 both carry usable content — return the body for either.
        const isAcceptable = response.ok || response.status === 406 || response.status === 304;
        if (!isAcceptable) {
            const errorMessage = data?.message || data?.error || `Error: ${response.status} - ${response.statusText}`;
            if (addAlerts)
                addAlerts(errorMessage, 'error');
            return null; // Return null on error
        }
        if (addAlerts)
            addAlerts(customMessage || GET_MESSAGE, 'success'); // Add success alert
        return data;
    }
    catch (error) {
        if (addAlerts)
            addAlerts(ERROR_MESSAGE + ' : ' + error, 'error'); // Add error alert
        console.error('GET request failed', error);
        return null; // Return null on network error
    }
    finally {
        if (stopLoading)
            stopLoading();
    }
};
export const post = async (url, body, addAlerts, startLoading, stopLoading, customMessage) => {
    if (startLoading)
        startLoading();
    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: getHeaders(url),
            body: JSON.stringify(body),
        });
        if (response.status === 401) {
            redirectOnUnauthorized();
            return null;
        }
        const data = await response.json();
        // Check for error status codes (404, 400, 500, etc.)
        if (!response.ok) {
            const errorMessage = data?.message || data?.error || `Error: ${response.status} - ${response.statusText}`;
            if (addAlerts)
                addAlerts(errorMessage, 'error');
            // Return the error response so callers can access validation errors
            return { ...data, isError: true, statusCode: response.status };
        }
        if (addAlerts)
            addAlerts(customMessage || POST_MESSAGE, 'success'); // Add success alert
        return data;
    }
    catch (error) {
        if (addAlerts)
            addAlerts(ERROR_MESSAGE + ' : ' + error, 'error'); // Add error alert
        console.error('POST request failed', error);
        return null; // Return null on network error
    }
    finally {
        if (stopLoading)
            stopLoading();
    }
};
export const put = async (url, body, addAlerts, startLoading, stopLoading, customMessage) => {
    if (startLoading)
        startLoading();
    try {
        const response = await fetch(url, {
            method: 'PUT',
            headers: getHeaders(url),
            body: JSON.stringify(body),
        });
        if (response.status === 401) {
            redirectOnUnauthorized();
            return null;
        }
        const data = await response.json();
        // Check for error status codes (404, 400, 500, etc.)
        if (!response.ok) {
            const errorMessage = data?.message || data?.error || `Error: ${response.status} - ${response.statusText}`;
            if (addAlerts)
                addAlerts(errorMessage, 'error');
            return null; // Return null on error
        }
        if (addAlerts)
            addAlerts(customMessage || PUT_MESSAGE, 'success'); // Add success alert
        return data;
    }
    catch (error) {
        if (addAlerts)
            addAlerts(ERROR_MESSAGE + ' : ' + error, 'error'); // Add error alert
        console.error('PUT request failed', error);
        return null; // Return null on network error
    }
    finally {
        if (stopLoading)
            stopLoading();
    }
};
export const del = async (url, addAlerts, startLoading, stopLoading, customMessage) => {
    if (startLoading)
        startLoading();
    try {
        const response = await fetch(url, {
            method: 'DELETE',
            headers: getHeaders(url),
        });
        if (response.status === 401) {
            redirectOnUnauthorized();
            return null;
        }
        const data = await response.json();
        // Check for error status codes (404, 400, 500, etc.)
        if (!response.ok) {
            const errorMessage = data?.message || data?.error || `Error: ${response.status} - ${response.statusText}`;
            if (addAlerts)
                addAlerts(errorMessage, 'error');
            return null; // Return null on error
        }
        if (addAlerts)
            addAlerts(customMessage || DELETE_MESSAGE, 'success'); // Add success alert
        return data;
    }
    catch (error) {
        if (addAlerts)
            addAlerts(ERROR_MESSAGE + ' : ' + error, 'error'); // Add error alert
        console.error('DELETE request failed', error);
        return null; // Return null on network error
    }
    finally {
        if (stopLoading)
            stopLoading();
    }
};
export const OldPostMutiFormData = async (url, bodyval, addAlerts, startLoading, stopLoading, customMessage) => {
    if (startLoading)
        startLoading();
    try {
        const authHeader = getHeaders(url);
        const headers = {};
        if (authHeader?.Authorization) {
            headers.Authorization = authHeader.Authorization;
        }
        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: bodyval
        });
        if (response.status === 401) {
            redirectOnUnauthorized();
            return null;
        }
        const data = await response.json();
        // Check for error status codes (404, 400, 500, etc.)
        if (!response.ok) {
            const errorMessage = data?.message || data?.error || `Error: ${response.status} - ${response.statusText}`;
            if (addAlerts)
                addAlerts(errorMessage, 'error');
            return null; // Return null on error
        }
        if (addAlerts)
            addAlerts(customMessage || POST_MESSAGE, 'success');
        return data;
    }
    catch (error) {
        if (addAlerts)
            addAlerts(ERROR_MESSAGE + ' : ' + error, 'error');
        console.error('FormData POST request failed', error);
        return null; // Return null on network error
    }
    finally {
        if (stopLoading)
            stopLoading();
    }
};
